import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { createCoupon, updateCoupon } from "../../../../services/couponfirebaseConnect";
import { errorToast } from "../../../../services/toastService";
import "./styles/CouponForm.css";

const initialFormState = {
  name: "",
  description: "",
  minProjectValue: "",
  offValidation: {
    condition: "PERCENTAGE",
    value: ""
  },
  status: "ACTIVE",
  type: "PROJECT",
  validity: {
    from: new Date(),
    to: new Date(),
  },
};

export default function CouponForm({ isOpen, onClose, onSubmit, initialData }) {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log('CouponForm received initialData:', initialData); // Debug log
    if (initialData && initialData.id) {
      console.log('Setting form data from initialData'); // Debug log
      // Map Firebase data structure to form structure
      const newFormData = {
        name: initialData.name || '',
        description: initialData.description || '',
        minProjectValue: initialData.minProjectValue || '',
        offValidation: {
          condition: initialData.offValidation?.condition || "PERCENTAGE",
          value: initialData.offValidation?.value || ""
        },
        status: initialData.status || "ACTIVE",
        type: initialData.type || "PROJECT",
        validity: {
          from: initialData.validity?.from instanceof Date ? 
            initialData.validity.from : 
            initialData.validity?.from?.toDate?.() || new Date(),
          to: initialData.validity?.to instanceof Date ? 
            initialData.validity.to : 
            initialData.validity?.to?.toDate?.() || new Date(),
        },
      };
      console.log('Setting form data to:', newFormData); // Debug log
      setFormData(newFormData);
    } else {
      console.log('Setting form data to initialFormState'); // Debug log
      setFormData(initialFormState);
    }
    setError("");
  }, [initialData, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData); // Debug log
    
    // Reset error state
    setError("");

    try {
      // Validate percentage
      if (formData.offValidation.condition === "PERCENTAGE" && parseFloat(formData.offValidation.value) > 100) {
        setError("Percentage discount cannot exceed 100%");
        return;
      }

      // Validate form data
      if (!formData) {
        throw new Error('No form data available');
      }

      // Ensure all required fields are present
      if (!formData.name || !formData.description || !formData.minProjectValue || 
          !formData.offValidation?.condition || !formData.offValidation?.value || 
          !formData.status || !formData.type || !formData.validity?.from || !formData.validity?.to) {
        throw new Error('Please fill in all required fields');
      }

      setLoading(true);

      try {
        // Call onSubmit with the validated form data
        await onSubmit(formData);
        // Don't close the form here - let the parent component handle it
      } catch (error) {
        // If the submission failed, show the error but don't close the form
        throw error;
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error); // Debug log
      const errorMessage = error.message || 'An error occurred while saving the coupon';
      errorToast(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Add loading state to submit button
  const getSubmitButtonText = () => {
    if (loading) return "Processing...";
    return initialData ? "Update Coupon" : "Create Coupon";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "value") {
      const numericValue = parseFloat(value);
      if (formData.offValidation.condition === "PERCENTAGE" && numericValue > 100) {
        setError("Percentage discount cannot exceed 100%");
        return;
      }
      setError("");
      setFormData(prev => ({
        ...prev,
        offValidation: {
          ...prev.offValidation,
          value
        }
      }));
    } else if (name === "condition") {
      setFormData(prev => ({
        ...prev,
        offValidation: {
          ...prev.offValidation,
          condition: value
        }
      }));
    } else if (name === "from" || name === "to") {
      setFormData((prev) => ({
        ...prev,
        validity: {
          ...prev.validity,
          [name]: new Date(value),
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="couponModal"
      overlayClassName="couponOverlay"
    >
      <div className="couponFormContainer">
        <div className="couponFormHeader">
          <h2>{initialData ? "Edit Coupon" : "Add New Coupon"}</h2>
          <button className="closeButton" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="couponForm">
          <div className="formGroup">
            <label>Coupon Code</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter coupon code"
              required
            />
          </div>

          <div className="formGroup">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter coupon description"
              required
            />
          </div>

          <div className="formGroup">
            <label>Minimum Project Value (SAR)</label>
            <input
              type="number"
              name="minProjectValue"
              value={formData.minProjectValue}
              onChange={handleChange}
              placeholder="Enter minimum project value"
              required
              min="0"
            />
          </div>

          <div className="formGroup">
            <label>Discount Type</label>
            <select
              name="condition"
              value={formData.offValidation.condition}
              onChange={handleChange}
              required
            >
              <option value="PERCENTAGE">Percentage</option>
              <option value="FLAT">Flat Amount</option>
            </select>
          </div>

          <div className="formGroup">
            <label>Discount Value</label>
            <input
              type="number"
              name="value"
              value={formData.offValidation.value}
              onChange={handleChange}
              placeholder={`Enter discount value ${formData.offValidation.condition === "PERCENTAGE" ? "(max 100%)" : ""}`}
              required
              min="0"
              max={formData.offValidation.condition === "PERCENTAGE" ? "100" : undefined}
            />
            {error && <div className="errorMessage">{error}</div>}
          </div>

          <div className="formGroup">
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Valid From</label>
            <input
              type="datetime-local"
              name="from"
              value={formData.validity?.from instanceof Date ? 
                formData.validity.from.toISOString().slice(0, 16) :
                formData.validity?.from?.toDate?.() ? 
                  formData.validity.from.toDate().toISOString().slice(0, 16) :
                  new Date().toISOString().slice(0, 16)
              }
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="formGroup">
            <label>Valid Until</label>
            <input
              type="datetime-local"
              name="to"
              value={formData.validity?.to instanceof Date ? 
                formData.validity.to.toISOString().slice(0, 16) :
                formData.validity?.to?.toDate?.() ? 
                  formData.validity.to.toDate().toISOString().slice(0, 16) :
                  new Date().toISOString().slice(0, 16)
              }
              onChange={handleChange}
              required
            />
          </div>

          <div className="formActions">
            <button type="button" className="cancelButton" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submitButton">
              {getSubmitButtonText()}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
